import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useContent } from '../context/ContentContext';
import ImageUpload from '../components/ImageUpload';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { logout } = useAuth();
  const { content, updateHeroSlides, updateFeatured, addPortfolioItem, updatePortfolioItem, deletePortfolioItem, addJournalEntry, updateJournalEntry, deleteJournalEntry, updateShop, uploadImage } = useContent();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('hero');

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const handleHeroSlideUpload = async (index, imageData) => {
    const newSlides = [...content.hero.slides];
    if (imageData) {
      newSlides[index] = imageData;
    } else {
      newSlides.splice(index, 1);
    }
    updateHeroSlides(newSlides);
  };

  const handleAddHeroSlide = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        const imageData = await uploadImage(file);
        updateHeroSlides([...content.hero.slides, imageData]);
      }
    };
    input.click();
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-sidebar">
        <div className="admin-sidebar-header">
          <h2>Timbalens CMS</h2>
          <button onClick={handleLogout} className="admin-logout-btn">
            Logout
          </button>
        </div>
        <nav className="admin-nav">
          <button
            className={activeSection === 'hero' ? 'active' : ''}
            onClick={() => setActiveSection('hero')}
          >
            Hero Section
          </button>
          <button
            className={activeSection === 'featured' ? 'active' : ''}
            onClick={() => setActiveSection('featured')}
          >
            Featured Sections
          </button>
          <button
            className={activeSection === 'portfolio' ? 'active' : ''}
            onClick={() => setActiveSection('portfolio')}
          >
            Portfolio
          </button>
          <button
            className={activeSection === 'journal' ? 'active' : ''}
            onClick={() => setActiveSection('journal')}
          >
            Journal
          </button>
          <button
            className={activeSection === 'shop' ? 'active' : ''}
            onClick={() => setActiveSection('shop')}
          >
            Shop
          </button>
        </nav>
      </div>

      <div className="admin-content">
        {activeSection === 'hero' && (
          <div className="admin-section">
            <h2>Hero Section</h2>
            <p className="admin-section-desc">Manage hero slider images</p>
            
            <div className="admin-hero-slides">
              {content.hero.slides.map((slide, index) => (
                <div key={index} className="admin-slide-item">
                  <ImageUpload
                    currentImage={slide}
                    onUpload={(image) => handleHeroSlideUpload(index, image)}
                    label={`Slide ${index + 1}`}
                  />
                </div>
              ))}
              <button onClick={handleAddHeroSlide} className="admin-add-button">
                + Add Slide
              </button>
            </div>
          </div>
        )}

        {activeSection === 'featured' && (
          <div className="admin-section">
            <h2>Featured Sections</h2>
            
            <div className="admin-featured-grid">
              <div className="admin-featured-item">
                <h3>Alps Photo Tour</h3>
                <ImageUpload
                  currentImage={content.featured.alps.image}
                  onUpload={(image) => updateFeatured('alps', { ...content.featured.alps, image })}
                />
                <div className="admin-form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={content.featured.alps.title}
                    onChange={(e) => updateFeatured('alps', { ...content.featured.alps, title: e.target.value })}
                  />
                </div>
                <div className="admin-form-group">
                  <label>Description</label>
                  <textarea
                    value={content.featured.alps.description}
                    onChange={(e) => updateFeatured('alps', { ...content.featured.alps, description: e.target.value })}
                    rows="3"
                  />
                </div>
              </div>

              <div className="admin-featured-item">
                <h3>Iceland Photo Tour</h3>
                <ImageUpload
                  currentImage={content.featured.iceland.image}
                  onUpload={(image) => updateFeatured('iceland', { ...content.featured.iceland, image })}
                />
                <div className="admin-form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={content.featured.iceland.title}
                    onChange={(e) => updateFeatured('iceland', { ...content.featured.iceland, title: e.target.value })}
                  />
                </div>
                <div className="admin-form-group">
                  <label>Description</label>
                  <textarea
                    value={content.featured.iceland.description}
                    onChange={(e) => updateFeatured('iceland', { ...content.featured.iceland, description: e.target.value })}
                    rows="3"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'portfolio' && (
          <div className="admin-section">
            <h2>Portfolio</h2>
            <button
              onClick={() => {
                const title = prompt('Enter title:');
                const category = prompt('Enter category:');
                if (title && category) {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/*';
                  input.onchange = async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const image = await uploadImage(file);
                      addPortfolioItem({ title, category, image, large: false });
                    }
                  };
                  input.click();
                }
              }}
              className="admin-add-button"
            >
              + Add Portfolio Item
            </button>

            <div className="admin-portfolio-grid">
              {content.portfolio.map((item) => (
                <div key={item.id} className="admin-portfolio-item">
                  <ImageUpload
                    currentImage={item.image}
                    onUpload={(image) => updatePortfolioItem(item.id, { image })}
                  />
                  <div className="admin-form-group">
                    <label>Title</label>
                    <input
                      type="text"
                      value={item.title}
                      onChange={(e) => updatePortfolioItem(item.id, { title: e.target.value })}
                    />
                  </div>
                  <div className="admin-form-group">
                    <label>Category</label>
                    <input
                      type="text"
                      value={item.category}
                      onChange={(e) => updatePortfolioItem(item.id, { category: e.target.value })}
                    />
                  </div>
                  <div className="admin-form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={item.large}
                        onChange={(e) => updatePortfolioItem(item.id, { large: e.target.checked })}
                      />
                      Large Item
                    </label>
                  </div>
                  <button
                    onClick={() => deletePortfolioItem(item.id)}
                    className="admin-delete-button"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeSection === 'journal' && (
          <div className="admin-section">
            <h2>Journal</h2>
            <button
              onClick={() => {
                const title = prompt('Enter title:');
                const date = prompt('Enter date:');
                if (title && date) {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/*';
                  input.onchange = async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const image = await uploadImage(file);
                      addJournalEntry({ title, date, image });
                    }
                  };
                  input.click();
                }
              }}
              className="admin-add-button"
            >
              + Add Journal Entry
            </button>

            <div className="admin-journal-list">
              {content.journal.map((entry) => (
                <div key={entry.id} className="admin-journal-item">
                  <ImageUpload
                    currentImage={entry.image}
                    onUpload={(image) => updateJournalEntry(entry.id, { image })}
                  />
                  <div className="admin-form-group">
                    <label>Title</label>
                    <input
                      type="text"
                      value={entry.title}
                      onChange={(e) => updateJournalEntry(entry.id, { title: e.target.value })}
                    />
                  </div>
                  <div className="admin-form-group">
                    <label>Date</label>
                    <input
                      type="text"
                      value={entry.date}
                      onChange={(e) => updateJournalEntry(entry.id, { date: e.target.value })}
                    />
                  </div>
                  <button
                    onClick={() => deleteJournalEntry(entry.id)}
                    className="admin-delete-button"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeSection === 'shop' && (
          <div className="admin-section">
            <h2>Shop</h2>
            
            <div className="admin-shop-grid">
              <div className="admin-shop-item">
                <h3>Presets</h3>
                <ImageUpload
                  currentImage={content.shop.presets.image}
                  onUpload={(image) => updateShop('presets', { ...content.shop.presets, image })}
                />
                <div className="admin-form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={content.shop.presets.title}
                    onChange={(e) => updateShop('presets', { ...content.shop.presets, title: e.target.value })}
                  />
                </div>
              </div>

              <div className="admin-shop-item">
                <h3>Prints</h3>
                <ImageUpload
                  currentImage={content.shop.prints.image}
                  onUpload={(image) => updateShop('prints', { ...content.shop.prints, image })}
                />
                <div className="admin-form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={content.shop.prints.title}
                    onChange={(e) => updateShop('prints', { ...content.shop.prints, title: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;

